/*
 * Sim Detail page.
 *
 * Single-sim deep-dive: header with name / level / difficulty / flags /
 * tags, headline KPIs (sent, click rate, 1st-stage, 2nd-stage, reported), per-user
 * engagement table.
 *
 * Calculations: aggregates SEND_LOG entries where simName matches, then
 * counts per status. Percentages are (count / sent * 100).
 *
 * Postgres mapping:
 *   SELECT sim_id, status, COUNT(*) FROM simulations_sent
 *   WHERE sim_id = ? GROUP BY status. Per-user table = same query
 *   grouped by (user_id, status).
 *
 * Integration hook: `useSimDetailQuery(simName, { period, scope })`
 * cache 30 min.
 */
import React, { useState, useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { motion } from 'motion/react';
import MainCard from '../../../components/cards/MainCard';
import CountUp from 'react-countup';
import { SIMS, SEND_LOG, DATA_EPOCH, simKpis } from '../data';
import useDashboardData, { logInPeriod } from '../hooks/useDashboardData';
import { toServerScope, userScopeIsEmails } from '../serverScope';
import { useTenancySimEvents } from '../tenancyEvents';
import { riskColor } from '../helpers';
import SimTagPanel from './SimTagPanel';
import DataTable from './DataTable';
import WidgetState from './WidgetState';
import { sendLogColumns, applyScopeToSendLog, FilterMulti, STATUS_META, STATUS_OPTIONS } from './SendHistoryPage';
import { tagsForSim, tagLabel } from '../tagAnalytics';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };
const EMPTY_LOG = [];

function MetricCard({ label, value, unit, sub, color }) {
    return (
        <motion.div variants={fadeUp} style={{ height: '100%', width: '100%', display: 'flex' }}>
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: 0.5 }}>{label}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: color || 'text.primary' }}>
                            <CountUp end={parseFloat(String(value).replace(/[^0-9.]/g, ''))} duration={1.4} decimals={String(value).includes('.') ? 1 : 0} />
                        </Typography>
                        {unit && <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>{unit}</Typography>}
                    </Box>
                    {sub && <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 'auto', pt: 0.5 }}>{sub}</Typography>}
                </Box>
            </MainCard>
        </motion.div>
    );
}

// Ascending signal bars read as intensity (low -> high) far better than
// equal dots, which look like status or pagination. Rising height encodes
// the 1-3 level pre-attentively; the green/amber/red fill keeps the
// existing semantic coding (3 = hardest = red).
function DiffDots({ diff }) {
    const fill = diff === 3 ? 'var(--red)' : diff === 2 ? 'var(--amber)' : 'var(--green)';
    return (
        <Box role="img" aria-label={`Difficulty ${diff} of 3`} sx={{ display: 'flex', gap: '3px', alignItems: 'flex-end', height: 12 }}>
            {[1, 2, 3].map((i) => (
                <Box key={i} aria-hidden="true" sx={{ width: 4, height: 4 + (i - 1) * 4, borderRadius: '1px', bgcolor: i <= diff ? fill : 'divider' }} />
            ))}
        </Box>
    );
}

export default function SimDetailPage({ go, drill, simName, period, startDate, endDate, scope, selfScope }) {
    const sim = SIMS.find((s) => s.name === simName);
    // A carried-over sim or tag filter could only empty a single-sim page (BD-1169).
    const peopleScope = useMemo(() => ({ ...scope, sim: undefined, tag: undefined }), [scope]);
    // A window older than the browser log reads the server (BD-1170).
    const tenancyEvents = useTenancySimEvents({ scope, selfScope, period, startDate, endDate });
    const sourceLog = tenancyEvents.scoped ? (tenancyEvents.rows || EMPTY_LOG) : SEND_LOG;
    // Every figure, the recipients table and the tag panel follow the selected period (BD-1003).
    const simEvents = useMemo(
        () => applyScopeToSendLog(logInPeriod(sourceLog.filter((e) => e.simName === simName), { period, startDate, endDate }), peopleScope),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sourceLog, simName, period, startDate, endDate, peopleScope, DATA_EPOCH]
    );
    // Same tenancy as the table: no tenancy here means the whole subtree (BD-1170).
    const simsQ = useDashboardData(
        { metric: 'simulations', aggregation: 'sims', period, startDate, endDate, scope: toServerScope({ ...scope, ...selfScope }) },
        { skip: !userScopeIsEmails(scope) }
    );
    const aggRow = Array.isArray(simsQ.data?.sims) ? simsQ.data.sims.find((r) => r.simName === simName) : null;
    const kpiQs = [simsQ, tenancyEvents];

    // Recipients table: the canonical send-log table (minus the Simulation
    // column - every row here is this sim), same filters as Send History.
    const recipientColumns = useMemo(() => sendLogColumns({ includeSim: false }), []);
    const [filterUsers, setFilterUsers] = useState([]);
    const [filterDepts, setFilterDepts] = useState([]);
    const [filterLocations, setFilterLocations] = useState([]);
    const [filterStatuses, setFilterStatuses] = useState([]);
    const recipientUsers = useMemo(() => {
        const m = new Map();
        for (const e of simEvents) if (!m.has(e.email)) m.set(e.email, `${e.firstName} ${e.lastName}`.trim() || e.email);
        return [...m.entries()].map(([email, name]) => ({ email, name })).sort((a, b) => a.name.localeCompare(b.name));
    }, [simEvents]);
    const recipientDepts = useMemo(() => [...new Set(simEvents.map((e) => e.dept))].sort(), [simEvents]);
    const recipientLocations = useMemo(() => [...new Set(simEvents.map((e) => e.location))].sort(), [simEvents]);
    const recipientRows = useMemo(() => simEvents.filter((e) => {
        if (filterUsers.length     && !filterUsers.some((u) => u.email === e.email)) return false;
        if (filterDepts.length     && !filterDepts.includes(e.dept))                 return false;
        if (filterLocations.length && !filterLocations.includes(e.location))         return false;
        if (filterStatuses.length  && !filterStatuses.includes(e.status))            return false;
        return true;
    }), [simEvents, filterUsers, filterDepts, filterLocations, filterStatuses]);

    if (!sim) {
        return (
            <Box sx={{ p: '24px', color: 'text.disabled', fontSize: 13 }}>Simulation not found.</Box>
        );
    }

    const { totalSent, clicked, secondStage, reported, clickRate, clickPct } = simKpis(aggRow, simEvents);
    const lv = clickPct >= 30 ? 'high' : clickPct >= 15 ? 'med' : 'low';
    const riskBadge = riskColor(lv);

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>

            <motion.div variants={fadeUp}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>{sim.name}</Typography>
                    {totalSent > 0 && (
                        <Box sx={{ display: 'inline-flex', px: '8px', py: '2px', borderRadius: '999px', fontSize: 10, fontWeight: 600, bgcolor: riskBadge.bg, color: riskBadge.text, border: `1px solid ${riskBadge.border}` }}>
                            {lv.charAt(0).toUpperCase() + lv.slice(1)} risk
                        </Box>
                    )}
                    <DiffDots diff={sim.diff} />
                    {sim.flag && (
                        <Typography sx={{ fontSize: 10, color: 'var(--red)', fontWeight: 600 }}>Flagged</Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {tagsForSim(sim.name).map((t) => (
                        <Box key={t} sx={{ fontSize: 10, fontWeight: 500, px: '8px', py: '2px', borderRadius: '4px', bgcolor: 'action.hover', color: 'text.secondary', border: '1px solid', borderColor: 'divider' }}>
                            {tagLabel(t)}
                        </Box>
                    ))}
                </Box>
            </motion.div>

            {/* 10 columns so five tiles share a row at md and up. */}
            <Grid container columns={10} spacing={1} alignItems="stretch" sx={{ '& > .MuiGrid-item': { display: 'flex' } }}>
                <Grid item xs={5} md={2}><WidgetState queries={kpiQs} fill><MetricCard label="Total Sent" value={totalSent} sub={period ? `${period} window` : 'Selected window'} /></WidgetState></Grid>
                <Grid item xs={5} md={2}><WidgetState queries={kpiQs} fill><MetricCard label="Click Rate" value={clickRate.toFixed(1)} unit="%" color="#FF5252" sub="Of total sends" /></WidgetState></Grid>
                <Grid item xs={5} md={2}><WidgetState queries={kpiQs} fill><MetricCard label="1st Stage" value={clicked} sub="Email link clicks" color="#FF5252" /></WidgetState></Grid>
                <Grid item xs={5} md={2}><WidgetState queries={kpiQs} fill><MetricCard label="2nd Stage" value={secondStage} sub="Landing page visits" color="#FFCA28" /></WidgetState></Grid>
                <Grid item xs={10} md={2}><WidgetState queries={kpiQs} fill><MetricCard label="Reported" value={reported} sub="Flagged as phishing" color="#66BB6A" /></WidgetState></Grid>
            </Grid>

            <SimTagPanel simName={sim.name} period={period} startDate={startDate} endDate={endDate} scope={peopleScope} />

            <motion.div variants={fadeUp}>
                <WidgetState queries={[tenancyEvents]}>
                <DataTable
                    title="Recipients"
                    exportName={`recipients ${simName}`}
                    columns={recipientColumns}
                    rows={recipientRows}
                    rowKey={(e) => `${e.email}-${e.sentAt}`}
                    onRowClick={(e) => drill('user-detail', `${e.firstName} ${e.lastName}`, { type: 'user', value: e.email })}
                    initialSort={{ by: 'sent', dir: 'desc' }}
                    storageKey="rbx-sim-recipients-cols"
                    emptyMessage="No send log entries for this simulation"
                    toolbar={
                        <>
                            <FilterMulti value={filterUsers}     onChange={setFilterUsers}     options={recipientUsers} placeholder="All users" getLabel={(u) => `${u.name} (${u.email})`} />
                            <FilterMulti value={filterDepts}     onChange={setFilterDepts}     options={recipientDepts} placeholder="All departments" />
                            <FilterMulti value={filterLocations} onChange={setFilterLocations} options={recipientLocations} placeholder="All locations" />
                            <FilterMulti value={filterStatuses}  onChange={setFilterStatuses}  options={STATUS_OPTIONS} placeholder="All statuses" getLabel={(s) => STATUS_META[s]?.label || s} />
                        </>
                    }
                />
                </WidgetState>
            </motion.div>

        </motion.div>
    );
}
